/** graph.arch.json reader + module launcher. Never classifies in-process. */

import { spawnSync } from 'node:child_process';
import * as fs from 'node:fs';
import * as path from 'node:path';
import type { VgGraph } from '../../schema.js';
import { ensureModuleEsmPackageJson, kernelDisabled } from '../../install/module-core.js';
import { haileModuleDir, haileModulePathOverride } from './haile-provider.js';
import type { HaileModuleSummary, HaileProfile, HaileSidecar, HaileSymbol } from './types.js';
import {
  DEFAULT_POLICY,
  DEFAULT_PROFILE,
  HAILE_ENGINE_VERSION,
  HAILE_IR,
  HAILE_MAGIC,
  HAILE_TAXONOMY,
  type HailePolicy,
} from './types.js';
import { architecturePolicyFor, isHailePolicy } from './policy-config.js';
import { applyArchitectureOverlays, loadArchitecturePolicy } from './policy-overlay.js';

function sidecarPathWithSuffix(graphPath: string, suffix: string): string {
  if (graphPath.endsWith('.json')) return `${graphPath.slice(0, -5)}${suffix}`;
  if (graphPath.endsWith('.snap')) return `${graphPath.slice(0, -5)}${suffix}`;
  return `${graphPath}${suffix}`;
}

/** `graph.arch.json` next to the graph — the public sidecar name. */
export function haileSidecarPathFor(graphPath: string): string {
  return sidecarPathWithSuffix(graphPath, '.arch.json');
}

/** Stale pre-rename sidecar (`graph.haile.json`) — removed on rebuild, never read or written. */
function stalePreRenameSidecarPathFor(graphPath: string): string {
  return sidecarPathWithSuffix(graphPath, '.haile.json');
}

export function deleteHaileSidecarFor(graphPath: string): void {
  for (const file of [haileSidecarPathFor(graphPath), stalePreRenameSidecarPathFor(graphPath)]) {
    try {
      fs.rmSync(file, { force: true });
    } catch {
      /* best-effort */
    }
  }
}

/** Installed module entry: VIBGRATE_ARCH_PATH or the modules cache. */
function resolveHaileModuleEntry(): string | null {
  if (kernelDisabled()) return null;
  const custom = haileModulePathOverride();
  if (custom) {
    const p = path.resolve(custom);
    try {
      if (fs.statSync(p).isDirectory()) {
        const idx = path.join(p, 'index.js');
        if (!fs.existsSync(idx)) return null;
        ensureModuleEsmPackageJson(p);
        return idx;
      }
    } catch {
      /* treat as a file path */
    }
    return fs.existsSync(p) ? p : null;
  }
  const idx = path.join(haileModuleDir(), 'index.js');
  if (!fs.existsSync(idx)) return null;
  ensureModuleEsmPackageJson(haileModuleDir());
  return idx;
}

export function serializeSidecar(sidecar: HaileSidecar): string {
  return `${JSON.stringify(sidecar)}\n`;
}

/** Write an already-built classify document. Does not classify. */
export function writeSidecarDocument(sidecar: HaileSidecar, graphPath: string, dest?: string): string | null {
  try {
    const file = dest ?? haileSidecarPathFor(graphPath);
    fs.mkdirSync(path.dirname(file), { recursive: true });
    const tmp = `${file}.${process.pid}.tmp`;
    fs.writeFileSync(tmp, serializeSidecar(sidecar));
    fs.renameSync(tmp, file);
    return file;
  } catch {
    return null;
  }
}

/**
 * Derive the classify file from an in-memory graph by launching the
 * installed module. Returns null when the module is missing — never a
 * TypeScript lexicon copy, never a PATH/`HAILE_BIN` fallback.
 *
 * The repository's `.vibgrate/architecture.toml` is read first: its baked
 * pack is handed to the module, its `[[overlay]]` tables are applied to the
 * document the module wrote. An overlay that does not validate throws
 * ArchitecturePolicyError before anything is classified — the caller fails
 * the architecture step loudly; a missing module stays a quiet null.
 */
export function writeHaileSidecarFor(
  graph: VgGraph,
  graphPath: string,
  options: { profile?: HaileProfile; policy?: HailePolicy; root?: string; out?: string } = {},
): string | null {
  const root = options.root ?? path.dirname(path.dirname(graphPath));
  // Fail loud on a bad overlay even when the module is absent: the file is
  // wrong whatever classifies, and the next `vg module install arch` must not
  // be the moment the team learns it.
  const document = loadArchitecturePolicy(root);
  try {
    const entry = resolveHaileModuleEntry();
    if (!entry) return null;
    const file = options.out ?? haileSidecarPathFor(graphPath);
    fs.mkdirSync(path.dirname(file), { recursive: true });
    const profile = options.profile ?? DEFAULT_PROFILE;
    // The policy comes from the flag, then the environment, then
    // `.vibgrate/architecture.toml` next to the graph's repository root.
    const policy = architecturePolicyFor(root, options.policy);
    const run = (pack: HailePolicy) =>
      spawnSync(process.execPath, [entry, 'sidecar', '--stdin', '--out', file, '--profile', profile, '--policy', pack], {
        input: JSON.stringify(graph),
        encoding: 'utf8',
        timeout: 120_000,
        maxBuffer: 64 * 1024 * 1024,
      });
    let result = run(policy);
    // An installed module published before the pack landed (vertical-v1)
    // refuses the id. Judge under the default and stamp what was evaluated —
    // the stamp, not the request, is what every reader prints.
    if (result.status !== 0 && policy !== DEFAULT_POLICY && /invalid --policy/.test(result.stderr ?? '')) {
      result = run(DEFAULT_POLICY);
    }
    if (result.status !== 0) return null;
    if (!fs.existsSync(file)) return null;
    if (document.overlays.length) {
      const written = JSON.parse(fs.readFileSync(file, 'utf8')) as HaileSidecar;
      if (written.magic !== HAILE_MAGIC || !Array.isArray(written.symbols)) return null;
      if (!isHailePolicy(written.policy)) written.policy = policy;
      applyArchitectureOverlays(written, document.overlays);
      if (!writeSidecarDocument(written, graphPath, file)) return null;
    }
    return file;
  } catch {
    return null;
  }
}

export function readHaileSidecar(
  graphPath: string,
  expect?: { corpusHash?: string; engineVersion?: string },
): HaileSidecar | null {
  try {
    const file = haileSidecarPathFor(graphPath);
    if (!fs.existsSync(file)) return null;
    const parsed = JSON.parse(fs.readFileSync(file, 'utf8')) as HaileSidecar;
    if (parsed.magic !== HAILE_MAGIC) return null;
    if (parsed.taxonomy !== HAILE_TAXONOMY) return null;
    if (expect?.corpusHash && parsed.corpus_hash !== expect.corpusHash) return null;
    if (expect?.engineVersion && parsed.engine_version !== expect.engineVersion) return null;
    if (!Array.isArray(parsed.symbols)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function findHaileSymbol(sidecar: HaileSidecar | null, nodeId: string): HaileSymbol | undefined {
  return sidecar?.symbols.find((s) => s.node_id === nodeId);
}

/** Test helper: a schema-valid empty classify document. Not a classifier. */
export function emptySidecar(corpusHash = '', profile: HaileProfile = DEFAULT_PROFILE): HaileSidecar {
  return {
    magic: HAILE_MAGIC,
    taxonomy: HAILE_TAXONOMY,
    ir: HAILE_IR,
    corpus_hash: corpusHash,
    engine_version: HAILE_ENGINE_VERSION,
    profile,
    symbols: [],
  };
}

export type { HaileModuleSummary };
